import { type FC } from 'react'
import Head from 'next/head'

import { Locales } from '@/utils/language'

export interface PaywallStructuredDataProps {
  /** 페이지 타입 */
  pageType: 'community' | 'post' | 'column' | 'tip'
  /** 현재 언어/로케일 */
  locale: Locales
  /** 콘텐츠 ID */
  contentId?: string | number
  /** 콘텐츠 카테고리 */
  contentCategory?: string
}

const PaywallStructuredData: FC<PaywallStructuredDataProps> = ({ pageType, locale, contentId, contentCategory }) => {
  // 언어별 기본 정보
  const getLocalizedInfo = () => {
    const isJapanese = locale === Locales.JA_JP
    const isKorean = locale === Locales.KO_KR

    return {
      siteName: isJapanese ? 'カンナムオンニ' : '강남언니',
      description: isJapanese
        ? '美容整形・美容外科の口コミ情報サイト'
        : '성형외과, 피부과 정보와 후기를 제공하는 뷰티 플랫폼',
      publisher: {
        '@type': 'Organization',
        name: isJapanese ? 'カンナムオンニ' : '강남언니',
        url: isJapanese ? 'https://gangnamunni.com/jp' : 'https://gangnamunni.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://gangnamunni.com/logo.png',
          width: 512,
          height: 512,
        },
      },
      provider: {
        '@type': 'Organization',
        name: 'Healing Paper Inc.',
        url: 'https://healingpaper.com',
      },
    }
  }

  // 페이지 타입별 구조화된 데이터 생성
  const generateStructuredData = () => {
    const { siteName, description, publisher, provider } = getLocalizedInfo()
    const baseUrl = locale === Locales.JA_JP ? 'https://gangnamunni.com/jp' : 'https://gangnamunni.com'

    const baseData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: siteName,
      description,
      publisher,
      provider,
      url: baseUrl,
      inLanguage: locale === Locales.JA_JP ? 'ja-JP' : 'ko-KR',
      isAccessibleForFree: true,
      hasPart: {
        '@type': 'WebPageElement',
        isAccessibleForFree: false,
        cssSelector: '.paywall-content',
      },
    }

    // 페이지 타입별 추가 데이터
    switch (pageType) {
      case 'community':
        return {
          ...baseData,
          '@type': 'CollectionPage',
          about: {
            '@type': 'Thing',
            name: locale === Locales.JA_JP ? '美容コミュニティ' : '뷰티 커뮤니티',
            description:
              locale === Locales.JA_JP
                ? 'ユーザー同士の美容情報交換コミュニティ'
                : '사용자 간의 뷰티 정보 교환 커뮤니티',
          },
          mainEntity: {
            '@type': 'ItemList',
            name: locale === Locales.JA_JP ? 'コミュニティ投稿' : '커뮤니티 게시글',
            ...(contentCategory && {
              about: {
                '@type': 'Thing',
                name: contentCategory,
              },
            }),
          },
        }

      case 'post':
      case 'column':
        return {
          ...baseData,
          '@type': 'Article',
          headline: locale === Locales.JA_JP ? '専門家コラム' : '전문가 칼럼',
          genre: contentCategory || (locale === Locales.JA_JP ? '美容情報' : '뷰티 정보'),
          ...(contentId && {
            identifier: contentId.toString(),
          }),
          author: {
            '@type': 'Organization',
            name: siteName,
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${baseUrl}/${pageType}s/${contentId}`,
          },
        }

      default:
        return baseData
    }
  }

  const structuredData = generateStructuredData()

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData, null, 2),
        }}
      />

      {/* 추가 메타 태그들 */}
      <meta name="robots" content="noindex, nofollow" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={getLocalizedInfo().siteName} />

      {/* 페이지별 특화 메타 태그 */}
      {pageType === 'community' && (
        <>
          <meta
            name="description"
            content={
              locale === Locales.JA_JP
                ? 'コミュニティの全てのコンテンツを見るにはログインが必要です。'
                : '커뮤니티의 모든 콘텐츠를 보려면 로그인이 필요합니다.'
            }
          />
          <meta
            property="og:description"
            content={
              locale === Locales.JA_JP
                ? '美容コミュニティで他のユーザーと情報交換をしましょう。'
                : '뷰티 커뮤니티에서 다른 사용자들과 정보를 교환해보세요.'
            }
          />
        </>
      )}

      {(pageType === 'post' || pageType === 'column') && (
        <>
          <meta
            name="description"
            content={
              locale === Locales.JA_JP
                ? '専門家が作成した高品質なコンテンツをご確認いただけます。'
                : '전문가가 작성한 고품질 콘텐츠를 확인할 수 있습니다.'
            }
          />
          <meta
            property="og:description"
            content={
              locale === Locales.JA_JP
                ? '美容専門家の詳細なコラムをお読みください。'
                : '뷰티 전문가의 상세한 칼럼을 읽어보세요.'
            }
          />
        </>
      )}
    </Head>
  )
}

export default PaywallStructuredData
